import { useRef, useState } from 'react';
import { Alert, Backdrop, Box, Button, Chip, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { Title } from 'react-admin';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadIcon from '@mui/icons-material/Upload';

import FilesBreadcrumb from './Breadcrumb';
import FileBrowserTable from './FileBrowserTable';
import NewFolderDialog from './NewFolderDialog';
import RenameDialog from './RenameDialog';
import MoveDialog from './MoveDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { useFileBrowser } from './useFileBrowser';
import type { FileBrowserItem } from './types';

const joinPath = (parentPath: string | undefined, name: string): string =>
  parentPath ? `${parentPath}/${name}` : name;

const FilesPage = () => {
  const {
    entry,
    path,
    items,
    isLoading,
    error,
    isUploading,
    uploadProgress,
    breadcrumb,
    navigateTo,
    navigateInto,
    createFolder,
    renameItem,
    deleteItems,
    moveItems,
    uploadFiles,
    downloadItems,
  } = useFileBrowser();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<FileBrowserItem | null>(null);
  const [moveTargets, setMoveTargets] = useState<FileBrowserItem[] | null>(null);
  const [deleteTargets, setDeleteTargets] = useState<FileBrowserItem[] | null>(null);

  const fileCount = items.filter(item => item.type === 'file').length;
  const folderCount = items.filter(item => item.type === 'folder').length;
  const disabledAtRootReason = 'Open a folder to add files';

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Files" />

      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Secure File Sharing
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Tooltip title={!entry ? disabledAtRootReason : ''}>
            <span>
              <Button
                variant="outlined"
                startIcon={<CreateNewFolderIcon />}
                disabled={!entry}
                onClick={() => setIsNewFolderOpen(true)}
              >
                New Folder
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!entry ? disabledAtRootReason : ''}>
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadIcon />}
                disabled={!entry}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload
              </Button>
            </span>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) void uploadFiles(files);
              e.target.value = '';
            }}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Files & Folders
          </Typography>
          <Chip
            size="small"
            label={`${fileCount} file${fileCount === 1 ? '' : 's'} · ${folderCount} folder${folderCount === 1 ? '' : 's'}`}
            sx={{ bgcolor: 'grey.100' }}
          />
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Files shared here are stored securely in S3 and are only accessible to authorized users of this
          workspace. To get started, select a folder below or click &quot;New Folder&quot; to create a new
          folder in this directory.
        </Alert>

        <FilesBreadcrumb segments={breadcrumb} onNavigate={navigateTo} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLoading ?
          (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) :
          (
            <FileBrowserTable
              items={items}
              entry={entry}
              onNavigateInto={navigateInto}
              onDownload={(items_) => { void downloadItems(items_); }}
              onRename={item => setRenameTarget(item)}
              onMove={targets => setMoveTargets(targets)}
              onMoveInto={(item, folder) => { void moveItems([item], joinPath(path, folder.name)); }}
              onDelete={targets => setDeleteTargets(targets)}
            />
          )}
      </Paper>

      <NewFolderDialog
        open={isNewFolderOpen}
        onClose={() => setIsNewFolderOpen(false)}
        onSubmit={createFolder}
      />

      {renameTarget && (
        <RenameDialog
          open={Boolean(renameTarget)}
          initialName={renameTarget.name}
          onClose={() => setRenameTarget(null)}
          onSubmit={newName => renameItem(renameTarget, newName)}
        />
      )}

      {moveTargets && entry && (
        <MoveDialog
          open={Boolean(moveTargets)}
          entry={entry}
          itemNames={moveTargets.map(t => t.name)}
          excludedPaths={moveTargets.filter(t => t.type === 'folder').map(t => joinPath(path, t.name))}
          currentPath={path}
          onClose={() => setMoveTargets(null)}
          onSubmit={destinationPath => moveItems(moveTargets, destinationPath)}
        />
      )}

      {deleteTargets && (
        <DeleteConfirmDialog
          open={Boolean(deleteTargets)}
          itemNames={deleteTargets.map(t => t.name)}
          onClose={() => setDeleteTargets(null)}
          onConfirm={() => deleteItems(deleteTargets)}
        />
      )}

      <Backdrop open={isUploading} sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">Uploading files…</Typography>
        {uploadProgress && (
          <Typography variant="body2">
            {uploadProgress.current}
            {' '}
            of 
            {' '}
            {uploadProgress.total}
          </Typography>
        )}
      </Backdrop>
    </Box>
  );
};

export default FilesPage;
